import { Cascader, Form, Input, Select } from "antd";
import { useEffect, useRef, useState } from "react";

import { useSearchDebounced } from "components/helpers/debounce";
import { useGetIds } from "reactQuery/hooks/useGetIds";

import { AutocompleteFormFieldProps, SearchQueryParamsProps } from "./types";

const COMPONENT_TYPES = {
    CASCADER: "CASCADER",
    INPUT: "INPUT",
    SELECT: "SELECT",
};
const DEFAULT_TEXT_KEY = "value";
const DEFAULT_VALUE_KEY = "id";

const AutocompleteFormField = ({
    allowArbitrary = false,
    allowMultiple = false,
    description = null,
    disabled = false,
    filterOption = null,
    highlight = null,
    id,
    label = "",
    optionFilterProp = "children",
    options = null,
    placeholderText = "Enter text to search...",
    searchAsUserTypes = false,
    searchQueryParams = [],
}: AutocompleteFormFieldProps) => {
    const componentType =
        allowArbitrary && !allowMultiple
            ? COMPONENT_TYPES.INPUT
            : searchQueryParams?.length > 1
            ? COMPONENT_TYPES.CASCADER
            : COMPONENT_TYPES.SELECT;

    const processedDataRef = useRef({}); // Keep track of processed data to avoid reprocessing on rerenders

    const [availableData, setAvailableData] = useState([]);
    const [initValueRetrieved, setInitValueRetrieved] = useState<boolean>(false);
    const [searchQueryString, setSearchQueryString] = useState<string>("");

    const queryMinChars = 1;
    const queryEnabled =
        !searchAsUserTypes || searchQueryString.length >= queryMinChars;

    const [queryHookInitParams, setQueryHookInitParams] = useState<any[]>(
        searchQueryParams.map((searchQueryParam, index) => {
            return {
                enabled: index === 0 ? !searchAsUserTypes : false,
                ...(searchQueryParam.queryFnParams || {}),
            };
        })
    );

    const debounceSearch = useSearchDebounced(setSearchQueryString);

    const searchQueryHooks = searchQueryParams?.map((queryParams, index) => {
        return queryParams.queryFn({
            ...(queryHookInitParams[index] || { enabled: false }),
        });
    });

    const getIdsMutation = useGetIds();

    const form = Form.useFormInstance();
    const initialIds = Array.isArray(form.getFieldValue(id))
        ? allowMultiple &&
          searchQueryParams?.length > 1 &&
          !Array.isArray(form.getFieldValue(id)[0])
            ? [form.getFieldValue(id)]
            : form.getFieldValue(id)
        : form.getFieldValue(id)
        ? [form.getFieldValue(id)]
        : null;

    const isMultiSelectCascader =
        componentType === COMPONENT_TYPES.CASCADER &&
        allowMultiple &&
        Array.isArray(initialIds) &&
        Array.isArray(initialIds[0]);

    const isSingleSelectCascader =
        componentType === COMPONENT_TYPES.CASCADER &&
        !isMultiSelectCascader &&
        Array.isArray(initialIds) &&
        initialIds.length > 0;

    const getOptionLabel = (option, optionsQueryParams) => {
        if (optionsQueryParams.textTemplate) {
            // This templating allows us to use templates like this:
            // "Company name: ${legal_name}. State: ${state_code}"
            // We can also use conditionals, like this: "Company name: ${legal_name}.[[ State: ${state_code}]]" (the entire square bracket part is conditional on state_code)
            return optionsQueryParams.textTemplate
                .replace(/\[\[(.*?)\]\]/g, (match, conditionalContent) => {
                    const hasAllKeys = conditionalContent
                        .match(/\${([^}]+)}/g)
                        ?.every((placeholder) => {
                            const key = placeholder.match(/\${([^}]+)}/)[1].trim();
                            return option[key];
                        });
                    return hasAllKeys
                        ? conditionalContent.replace(/\${([^}]+)}/g, (_, key) => {
                              const trimmedKey = key.trim();
                              return option[trimmedKey] ? option[trimmedKey] : "";
                          })
                        : "";
                })
                .replace(/\${([^}]+)}/g, (_, key) => {
                    const trimmedKey = key.trim();
                    return option[trimmedKey] ? option[trimmedKey] : "";
                });
        }

        return option[optionsQueryParams.textKey || "value"] || option.id;
    };

    const addUnsetOption = (unsetConfig, options, parentNode) => {
        const label = `Unset ${
            parentNode?.label ? `${parentNode.label} ` : ""
        } Filters`;
        options.unshift({
            value: unsetConfig.idModifier(parentNode?.value),
            label: label,
            isLeadf: true,
        });
        if (options.length === 1) {
            options.push({ value: 0, label: "", isLeaf: true });
        }
        return options;
    };

    const findNode = (nodes, targetValue, targetDepth, depth = 0) => {
        for (const node of nodes) {
            if (node.value === targetValue && depth === targetDepth) {
                return node;
            }

            if (node.children && depth < targetDepth) {
                return findNode(node.children, targetValue, targetDepth, depth + 1);
            }
        }
        return null;
    };

    const findAndAttachChildren = (
        nodes,
        targetValue,
        targetDepth,
        childrenToAdd,
        depth = 0
    ) => {
        return nodes.map((node) => {
            if (node.value === targetValue && depth === targetDepth) {
                if (childrenToAdd && childrenToAdd.length > 0) {
                    return {
                        ...node,
                        loading: false,
                        isLeaf: false,
                        children: childrenToAdd,
                    };
                } else {
                    return {
                        ...node,
                        loading: false,
                        isLeaf: true,
                    };
                }
            }

            if (node.children && node.children.length > 0) {
                return {
                    ...node,
                    children: findAndAttachChildren(
                        node.children,
                        targetValue,
                        targetDepth,
                        childrenToAdd,
                        depth + 1
                    ),
                };
            }

            return node;
        });
    };

    const handleQueryData = (queryData, queryHookParams, index) => {
        const textKey = queryHookParams.textKey || DEFAULT_TEXT_KEY;
        const valueKey = queryHookParams.valueKey || DEFAULT_VALUE_KEY;
        let newOptions = [];
        const defaultLeafValue =
            componentType === COMPONENT_TYPES.CASCADER
                ? index < searchQueryParams.length - 1
                    ? false
                    : true
                : undefined;
        if (Array.isArray(queryData)) {
            // Handle endpoints that return an array
            newOptions = queryData.map((option) => ({
                value: option[valueKey],
                label: getOptionLabel(option, queryHookParams),
                isLeaf: option.no_children ? true : defaultLeafValue,
                originalOption: option,
            }));
        } else if (typeof queryData === "object" && queryData !== null) {
            // Handle endpoints that return an object
            newOptions = Object.entries(queryData).map(([key, val]) => {
                const option =
                    typeof val === "object" && val !== null
                        ? { ...val, [valueKey]: key }
                        : { [textKey]: val, [valueKey]: key };

                return {
                    value: key,
                    label: getOptionLabel(option, queryHookParams),
                    isLeaf: defaultLeafValue,
                    originalOption: option,
                };
            });
        }
        return newOptions;
    };

    // Set query hook query param when searchQueryString changes
    useEffect(() => {
        if (searchQueryString) {
            setQueryHookInitParams((prevData) => [
                {
                    ...(prevData[0] || {}),
                    enabled: true,
                    query: searchQueryString,
                },
                ...(prevData.length > 1 ? prevData.slice(1) : []),
            ]);
        }
    }, [searchQueryString]);

    // Get initial display values for preselected IDs
    useEffect(() => {
        if (!initialIds || initValueRetrieved || !searchQueryParams.length) {
            return;
        }

        if (isMultiSelectCascader) {
            const lastIds = initialIds.map((innerArray) => innerArray.at(-1));
            getIdsMutation.mutate({
                [searchQueryParams[searchQueryParams.length - 1].id]: lastIds,
            });
            setInitValueRetrieved(true);
        } else if (
            isSingleSelectCascader ||
            (searchAsUserTypes && initialIds.length > 0)
        ) {
            const requiresGetIds =
                isSingleSelectCascader || allowMultiple
                    ? initialIds.some((val) => typeof val !== "object")
                    : typeof initialIds !== "object";

            if (requiresGetIds) {
                if (componentType === COMPONENT_TYPES.CASCADER) {
                    const idsToGet = initialIds.reduce(
                        (rest, item, index) =>
                            index === 0
                                ? rest
                                : {
                                      ...rest,
                                      [searchQueryParams[index].id]: [item],
                                  },
                        {}
                    );
                    if (idsToGet && Object.keys(idsToGet).length > 0) {
                        getIdsMutation.mutate(idsToGet);
                    }
                } else {
                    getIdsMutation.mutate({ [id]: initialIds });
                }
            }
            setInitValueRetrieved(true);
        }
    }, [initialIds]);

    // Process initial display values once query data is available
    useEffect(() => {
        if (!searchQueryParams) {
            return;
        }
        for (var i = 0; i < searchQueryParams.length; i++) {
            const currentId = searchQueryParams[i].id || (i === 0 ? id : null);
            if (currentId === undefined || currentId === null) {
                continue;
            }
            const retrievedIdData = getIdsMutation.data?.[currentId];

            if (!retrievedIdData) {
                continue;
            }

            const initialOptions = handleQueryData(
                retrievedIdData,
                searchQueryParams[i],
                i
            );
            let valueToSet;
            if (componentType === COMPONENT_TYPES.CASCADER) {
                let nestedValues = availableData;
                if (isMultiSelectCascader) {
                    initialIds.map((initialId) => {
                        if (initialId[initialId.length - 1] < 0) {
                            // Unset Option
                            const parentValue = initialId[initialId.length - 2];
                            const finalOptions = addUnsetOption(
                                searchQueryParams[i].unsetOption,
                                initialOptions,
                                findNode(availableData, parentValue, i - 1)
                            );
                            nestedValues = findAndAttachChildren(
                                nestedValues,
                                parentValue,
                                i - 1,
                                finalOptions
                            );
                        } else {
                            nestedValues = findAndAttachChildren(
                                nestedValues,
                                initialId[i - 1],
                                i - 1,
                                initialOptions
                            );
                        }
                        setAvailableData(nestedValues);
                    });
                } else {
                    nestedValues = findAndAttachChildren(
                        availableData,
                        initialIds[i - 1],
                        0,
                        initialOptions
                    );
                    setAvailableData(nestedValues);
                }
                valueToSet = initialIds;
            } else {
                setAvailableData(initialOptions);
                valueToSet = allowMultiple ? initialOptions : initialOptions[0];
            }
            if (valueToSet) {
                form.setFieldsValue({ [id]: valueToSet });
            }

            if (i === 0 && componentType !== COMPONENT_TYPES.CASCADER) {
                break;
            }
        }
    }, [getIdsMutation.data]);

    useEffect(() => {
        if (!searchQueryHooks || searchQueryParams.length === 0) {
            return;
        }

        searchQueryHooks.forEach((queryHook, index) => {
            const queryData = queryHook.data;

            if (queryData && processedDataRef.current[index] !== queryData) {
                processedDataRef.current[index] = queryData;

                const queryHookParams =
                    searchQueryParams[index] || ({} as SearchQueryParamsProps);

                const newOptions = handleQueryData(queryData, queryHookParams, index);

                if (index === 0) {
                    setAvailableData((existingOptions) => {
                        const combinedOptions = [...newOptions, ...existingOptions];
                        const uniqueOptionsMap = new Map(
                            combinedOptions.map((item) => [item.value, item])
                        );
                        return Array.from(uniqueOptionsMap.values());
                    });
                } else {
                    const parentQueryConfig = searchQueryParams[index - 1];
                    const queryKeyForParent =
                        parentQueryConfig.queryKey || DEFAULT_VALUE_KEY;
                    const parentValue = queryHookInitParams[index]?.[queryKeyForParent];

                    if (parentValue !== undefined) {
                        const finalOptions =
                            queryHookParams.unsetOption &&
                            queryHookParams.unsetOption.parentValues?.includes(
                                parentValue
                            )
                                ? addUnsetOption(
                                      queryHookParams.unsetOption,
                                      newOptions,
                                      findNode(availableData, parentValue, index - 1)
                                  )
                                : newOptions;
                        setAvailableData((currentData) =>
                            findAndAttachChildren(
                                currentData,
                                parentValue,
                                index - 1,
                                finalOptions
                            )
                        );
                    }
                }
            }
        });
    }, [searchQueryHooks.map((hook) => hook.data)]);

    const loadCascaderData = async (selectedOptions: any[]) => {
        const selectedOptionsDepth = selectedOptions.length;
        const targetOption = selectedOptions[selectedOptionsDepth - 1];

        const currentQueryConfig = searchQueryParams[selectedOptionsDepth - 1];
        const nextQueryConfig = searchQueryParams[selectedOptionsDepth];

        const queryKey = currentQueryConfig.queryKey || DEFAULT_VALUE_KEY;

        if (!nextQueryConfig || targetOption.originalOption?.no_children) {
            selectedOptions[selectedOptionsDepth - 1].isLeaf = true;
            return;
        }

        setQueryHookInitParams((prevData) => {
            if (prevData.length < selectedOptionsDepth - 1) {
                return prevData;
            }

            const currentParams = {
                enabled: true,
                [queryKey]: targetOption.value,
            };

            if (prevData.length === selectedOptionsDepth) {
                return [...prevData, currentParams];
            }

            return prevData.map((params, index) => {
                if (index === selectedOptionsDepth) {
                    return {
                        ...params,
                        ...currentParams,
                    };
                }
                return params;
            });
        });

        setAvailableData((prevAvailableData) => {
            return prevAvailableData.map((data) => {
                if (data.value === targetOption.value) {
                    return {
                        ...data,
                        loading: true,
                    };
                }
                return data;
            });
        });
    };

    const renderCascader = () => (
        <Cascader
            disabled={disabled}
            options={availableData}
            loadData={loadCascaderData}
            placeholder={placeholderText}
            changeOnSelect
            multiple={allowMultiple}
        />
    );
    const renderInput = () => <Input allowClear={true} />;

    const renderSelect = () => {
        if (options) {
            return (
                <Select
                    defaultValue={placeholderText}
                    options={options}
                    optionFilterProp="label"
                />
            );
        } else {
            const selectOptionsQuery = searchQueryHooks?.[0] || {};
            return (
                <Select
                    allowClear
                    labelInValue
                    disabled={disabled}
                    filterOption={
                        filterOption
                            ? filterOption
                            : searchAsUserTypes
                            ? false
                            : (input, option) => {
                                  const serializedOption = String(
                                      option?.label
                                  ).toLowerCase();
                                  return serializedOption.includes(input.toLowerCase());
                              }
                    }
                    mode={
                        allowArbitrary ? "tags" : allowMultiple ? "multiple" : undefined
                    }
                    onSearch={searchAsUserTypes ? debounceSearch : undefined}
                    optionFilterProp={optionFilterProp}
                    placeholder={placeholderText}
                    showSearch
                    {...(allowArbitrary || (allowMultiple && searchAsUserTypes)
                        ? { tokenSeparators: [","], notFoundContent: null }
                        : {
                              notFoundContent: selectOptionsQuery.isLoading
                                  ? "Loading..."
                                  : "No results found",
                          })}
                    options={
                        allowArbitrary || (!queryEnabled && !initialIds)
                            ? placeholderText && placeholderText !== ""
                                ? [
                                      {
                                          value: "disabled",
                                          label: placeholderText,
                                          disabled: true,
                                      },
                                  ]
                                : []
                            : queryEnabled && selectOptionsQuery.isLoading
                            ? [
                                  {
                                      value: "disabled",
                                      label: "Loading...",
                                      disabled: true,
                                  },
                              ]
                            : selectOptionsQuery.isSuccess || availableData.length > 0
                            ? availableData
                            : [
                                  {
                                      value: "disabled",
                                      label: "There was a problem loading options",
                                      disabled: true,
                                  },
                              ]
                    }
                />
            );
        }
    };

    return (
        <>
            {highlight ? <>{highlight}</> : null}
            <Form.Item extra={description} label={label} name={id}>
                {componentType === COMPONENT_TYPES.INPUT
                    ? renderInput()
                    : componentType === COMPONENT_TYPES.CASCADER
                    ? renderCascader()
                    : componentType === COMPONENT_TYPES.SELECT
                    ? renderSelect()
                    : null}
            </Form.Item>
        </>
    );
};

export default AutocompleteFormField;
